import { Flex, FlexProps } from '@chakra-ui/react'
import { startCase } from 'lodash-es'
import { ReactNode } from 'react'
import { borderRadiusMap, useTheme } from 'utils'
import { InputContainerProps } from '.'
import InputContainer from './InputContainer'

export interface Props<T extends string> extends Omit<InputContainerProps, 'onChange'> {
  borderRadius?: keyof typeof borderRadiusMap
  buttonProps?: Omit<FlexProps, 'children' | 'onClick'>
  containerProps?: Omit<FlexProps, 'borderRadius' | 'children'> & {
    borderRadius?: keyof typeof borderRadiusMap
  }
  value: string
  options: Option<T>[]
  onChange: (value: T) => void
}

type Option<T extends string> = OptionObject<T> | T

interface OptionObject<T extends string> {
  value: T
  label: ReactNode
}

export default function ToggleButton<T extends string>({
  buttonProps,
  containerProps,
  value,
  options,
  onChange,
  ...rest
}: Props<T>) {
  const { theme } = useTheme()
  const palette = 'primary'

  const borderRadius = containerProps?.borderRadius ?? 'sm'
  const radiusPx = Number(borderRadiusMap[borderRadius].split('px')[0])

  return (
    <InputContainer {...rest}>
      <Flex
        borderRadius={borderRadius}
        borderWidth="1px"
        py={1}
        px={0.5}
        borderColor="gray.300"
        {...containerProps}
      >
        {options.map(o => {
          const v = typeof o === 'string' ? o : o.value
          const l = typeof o === 'string' ? startCase(o) : o.label

          return (
            <Flex
              key={v}
              borderRadius={`${radiusPx - 2}px`}
              py={2}
              px={4}
              mx={0.5}
              bg={v === value ? `${palette}.500` : 'transparent'}
              color={v === value ? 'white' : 'inherit'}
              cursor="pointer"
              textStyle="bodySmall"
              _hover={{
                bg: v === value ? '' : `${palette}.${theme === 'light' ? '2' : '7'}00`,
              }}
              onClick={e => {
                e.stopPropagation()
                onChange(v)
              }}
              {...buttonProps}
            >
              {l}
            </Flex>
          )
        })}
      </Flex>
    </InputContainer>
  )
}
